import { useEffect, useState } from 'preact/hooks';
import { isYes } from '@util/common.js';
import CustomizeFullProgressBar from './customize-full-progress-bar.jsx';
import { deepSignal } from 'deepsignal';
import { CHARITY } from '@util/const.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import Picker from '@components/input/picker.input.jsx';
import {
  MaxProgressBar,
  toMaxProgressBarTextColor,
} from '@playlive/overlay-core/progress-bars/max';
import { toColor } from '@playlive/overlay-core/colors';
import DemoAnimationsDropdown from '@components/overlays/shared/demo-animations-dropdown.jsx';
import usePreviewAnimations from '@hooks/usePreviewAnimations.js';
import useCommon from '@hooks/useCommon.js';
import useDemoProgressBarData from '@hooks/useDemoProgressBarData.js';
import useAdjustableAmounts from '@hooks/useAdjustableAmounts.js';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  classicDonorOptions: 'yes',
  textColor: 'light-aqua',
  bgColor: 'default-red',
  showChallenges: 'yes',
  showRewards: 'yes',
  showDonors: 'yes',
  showScroller: 'yes',
  scrollerPosition: 'bottom',
  donationTimeOffset: 1,
  showAllDonations: 'no',
  showOriginalGoal: 'no',
  ccamMode: 'no',
  ccamFlagHorizontalPosition: 'right',
  ccamFlagVerticalPosition: 'bottom',
});

const useBetaProgressBars = isYes(
  import.meta.env.VITE_BETA_PROGRESS_BARS || 'no'
);
const urlBase = useBetaProgressBars ? 'progress-bars/max' : 'max';

export default function MaxProgressBarPage() {
  const { overlayURL, setConfigState, overlayInfo, setConfigKeyValue } =
    useCommon({ config, urlBase });
  const [showMilestones, setMilestones] = useState(false);
  const { currentAmount, goalAmount, updateAmounts, setCurrentAmount } =
    useAdjustableAmounts();

  const scrollPositions = [
    { name: 'Top', value: 'top' },
    { name: 'Bottom', value: 'bottom' },
  ];

  const { demoDonations, demoMilestones, demoTargets, demoRewards } =
    useDemoProgressBarData(config);

  useEffect(() => {
    if (isYes(config.showOriginalGoal)) {
      updateAmounts({
        newCurrentAmount: 1900,
        newGoalAmount: 2000,
      });
    } else {
      updateAmounts({
        newCurrentAmount: 750,
        newGoalAmount: 1000,
      });
    }
  }, [config.showOriginalGoal]);

  const { isPreviewingAnimation, startAnimation } = usePreviewAnimations({
    setMilestones,
    setCurrentAmount,
  });

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col items-center justify-center  overflow-x-hidden">
        <div className="example_overlay relative select-none w-full">
          <ScaledOverlayWrapper
            addPadding={false}
            defaultScale={'scale-100'}
            allowScaling={false}
            addPosition={'absolute top-0'}
            innerClassName={'h-[350px]'}
          >
            <div className="absolute top-0 z-10 max-w-full w-full">
              <MaxProgressBar
                originalGoalAmount={1000}
                ccamFlagVerticalPosition={config.ccamFlagVerticalPosition}
                ccamFlagHorizontalPosition={config.ccamFlagHorizontalPosition}
                ccamMode={isYes(config.ccamMode)}
                className="shadow-2xl"
                currentAmount={currentAmount}
                goalAmount={goalAmount}
                backgroundColor={toColor(config.bgColor)}
                textColor={toMaxProgressBarTextColor(config.textColor)}
                showOriginalGoal={isYes(config.showOriginalGoal)}
                showScroller={isYes(config.showScroller)}
                showMilestones={showMilestones}
                showDonors={isYes(config.showDonors)}
                showChallenges={isYes(config.showChallenges)}
                showRewards={isYes(config.showRewards)}
                scrollerPosition={config.scrollerPosition}
                rewards={demoRewards}
                donations={demoDonations}
                targets={demoTargets}
                milestones={demoMilestones}
                demoShowMilestones={showMilestones}
              />
            </div>
          </ScaledOverlayWrapper>
          <DemoAnimationsDropdown
            onChosen={startAnimation}
            disabled={isPreviewingAnimation}
          />
        </div>

        <CustomizeFullProgressBar
          config={config}
          setConfigKeyValue={setConfigKeyValue}
          setConfigState={setConfigState}
          overlayURL={overlayURL}
          setMilestones={setMilestones}
          setCurrentAmount={setCurrentAmount}
          overlayName={overlayInfo.name}
          obsDimensions={overlayInfo.obsDimensions}
          childrenSlot="scroller"
        >
          <Picker
            label="Scroller Position"
            items={scrollPositions}
            onChange={setConfigState}
            currentValue={config.scrollerPosition}
            fieldName="scrollerPosition"
          />
        </CustomizeFullProgressBar>
      </div>
    </div>
  );
}
